import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { PaymentMethodCard } from 'src/components/PaymentMethod';
import { useAppContext } from 'src/contexts/AppContext';
import { useContent } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { vars } from 'src/core/themes';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { useIsAdyenConfigured } from 'src/hooks/api/useAdyenPaymentSession';
import { useGetBuyerCredit } from 'src/hooks/api/useGetBuyerCredit';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Detail } from 'src/modals/common';
import { DetailSection } from 'src/modals/common/DetailSection';
import { useRetailSaleContext } from 'src/modals/RetailSale/RetailSaleLocalContext';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { ExternalPaymentMethodType, Feature } from 'src/WebApiController';

import { PaymentMethodChoice, RetailSaleFormValues } from '../types';
import { ExternalPaymentSection } from './ExternalPaymentSection';
import { PartialPaymentInput } from './PartialPaymentInput';
import * as styles from './RetailSaleCheckoutStep.css';

// Sentinel RadioGroup value for the "Add new card" option; maps to a null
// selectedStoredPaymentMethodId (charge a brand-new card rather than a saved one).
const ADD_NEW_CARD_VALUE = 'add-new-card';

/**
 * Shared PAYMENT METHOD section for the retail-sale checkout. Rendered
 * unchanged by both the single-listing (`RetailSaleCheckoutStep`) and the
 * multi-listing (`MultiListingRetailSaleCheckoutStep`) flows so the payment
 * chrome — mode radio, partial payment, saved cards, external payment form,
 * and the partial coverage dialog — stays in lockstep across the two entry
 * points.
 */
export const RetailSalePaymentSection = () => {
  const {
    disabled,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    setSelectedStoredPaymentMethodId,
    isPartialPayment,
    setIsPartialPayment,
    partialAmount,
    setPartialAmount,
  } = useRetailSaleContext();

  const { loginContext } = useAppContext();
  const { watch, setValue, formState } = useFormContext<RetailSaleFormValues>();

  // Hide the "Paid in POS" (Adyen) option unless the seller has an active Adyen
  // processor configured. `isLoading` distinguishes a pending lookup from a
  // resolved "not configured" so we don't flash-hide the option while loading.
  const { data: isAdyenConfigured, isLoading: isAdyenConfigLoading } =
    useIsAdyenConfigured();
  const showAdyenOption = isAdyenConfigured === true;

  const totalNetProceeds = watch('totalNetProceeds');
  const marketplacePaymentLines = watch('marketplacePaymentLines');
  const paymentMethodChoice =
    watch('paymentMethodChoice') ?? (showAdyenOption ? 'adyen' : 'external');
  const currencyCode =
    watch('currencyCode') ??
    loginContext?.user?.activeAccount.currencyCode ??
    'USD';

  const totalAmount =
    (totalNetProceeds ?? 0) +
    (marketplacePaymentLines ?? []).reduce(
      (sum, l) => sum + (l.amount ?? 0),
      0
    );

  const paymentMsg = useContent(ContentId.Payment);
  const adyenWithNoCvvMsg = useContent(ContentId.AdyenWithNoCvv);
  const adyenWithCvvMsg = useContent(ContentId.AdyenWithCvv);
  const creditPaymentNote = useContent(ContentId.PaidViaCustomerCredit);
  const customerRemainingCreditLabel = useContent(
    ContentId.CustomerRemainingCredit
  );
  const paymentAmountText = useContent(ContentId.PaymentAmount);
  const paymentMethodText = useContent(ContentId.PaymentMethod);
  const completeUnpaid = useContent(ContentId.CompleteAsUnpaid);

  const { data: savedPaymentMethods = [] } = useGetBuyerPaymentMethods(
    selectedBuyer?.buyerEmail
  );

  // Gate the "Paid Externally" option behind the RetailSale feature flag.
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  // POS-8726: Fetch buyer credit balance for "Pay by Customer Credit" option
  const { data: buyerCredit } = useGetBuyerCredit(
    selectedBuyer?.buyerEmail,
    !hasRetailSaleFeature,
    currencyCode // Convert credit to the sale's currency for display
  );
  const availableCreditBalance = buyerCredit?.balance ?? 0;

  const isExternalPayment = paymentMethodChoice === 'external';

  const handlePaymentChoiceChange = useCallback(
    (value: string) => {
      const choice = value as PaymentMethodChoice;
      setValue('paymentMethodChoice', choice);

      if (choice === 'external') {
        const today = new Date().toISOString().split('T')[0];
        setValue('externalPaymentDate', today);
        setValue('externalPaymentAmount', totalAmount > 0 ? totalAmount : null);
      } else if (choice === 'credit') {
        // POS-8726: Set credit payment fields. Don't set externalPaymentAmount here —
        // it's derived from the current total at submission time so it stays in sync
        // when the user changes quantity/listings after selecting credit.
        setValue(
          'externalPaymentMethodType',
          ExternalPaymentMethodType.CustomerCredit
        );
        setValue('externalPaymentNote', creditPaymentNote);
        setValue('externalPaymentDate', new Date().toISOString().split('T')[0]);
      } else {
        setValue('externalPaymentMethodType', null);
        setValue('externalPaymentNote', null);
        setValue('customPaymentTypeName', null);
        setValue('customPaymentTypeId', null);
        setValue('externalPaymentAmount', null);
        setValue('externalPaymentDate', null);
      }
    },
    [creditPaymentNote, setValue, totalAmount]
  );

  // When Adyen isn't configured (or the feature is off), the "Paid in POS"
  // option is hidden — fall the default 'adyen' selection back to 'external'.
  useEffect(() => {
    if (isAdyenConfigLoading || showAdyenOption) return;
    if (paymentMethodChoice !== 'adyen') return;

    setValue('paymentMethodChoice', 'external');
    setValue('externalPaymentDate', new Date().toISOString().split('T')[0]);
    setValue('externalPaymentAmount', totalAmount > 0 ? totalAmount : null);
  }, [
    isAdyenConfigLoading,
    showAdyenOption,
    paymentMethodChoice,
    setValue,
    totalAmount,
  ]);

  const onToggleUnpaid = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const updatedPaymentChoice = e.target.checked
        ? 'unpaid'
        : (formState.defaultValues?.paymentMethodChoice ?? 'adyen');
      handlePaymentChoiceChange(updatedPaymentChoice);
    },
    [formState.defaultValues?.paymentMethodChoice, handlePaymentChoiceChange]
  );

  return (
    <>
      <DetailSection name={paymentMsg}>
        <Checkbox
          checked={paymentMethodChoice === 'unpaid'}
          label={completeUnpaid}
          onChange={onToggleUnpaid}
          labelPosition="right"
        />
        {paymentMethodChoice !== 'unpaid' && (
          <>
            <Detail
              label={paymentAmountText}
              detail={
                <PartialPaymentInput
                  disabled={disabled}
                  totalAmount={totalAmount}
                  currencyCode={currencyCode}
                  isPartialPayment={isPartialPayment}
                  partialAmount={partialAmount}
                  onIsPartialPaymentChange={setIsPartialPayment}
                  onPartialAmountChange={setPartialAmount}
                />
              }
            />
            <Detail
              label={paymentMethodText}
              detail={
                <Stack direction="column" gap="m">
                  <RadioGroup
                    value={paymentMethodChoice}
                    onValueChange={handlePaymentChoiceChange}
                    disabled={disabled}
                  >
                    <Stack width="full" direction="column" gap="m">
                      {showAdyenOption && (
                        <>
                          <Radio
                            alignItems="start"
                            variant="outline"
                            value="adyen"
                            label={adyenWithNoCvvMsg}
                            containerClassName={styles.paymentItem}
                            labelSubtext={
                              paymentMethodChoice === 'adyen' && (
                                <Stack
                                  width="full"
                                  direction="column"
                                  gap="m"
                                  style={{ paddingTop: vars.spacing.m }}
                                >
                                  <RadioGroup
                                    value={selectedStoredPaymentMethodId}
                                    onValueChange={(value) =>
                                      setSelectedStoredPaymentMethodId(value)
                                    }
                                    disabled={disabled}
                                  >
                                    {savedPaymentMethods.map((card) => (
                                      <Radio
                                        key={card.storedPaymentMethodId}
                                        value={card.storedPaymentMethodId}
                                        variant="outline"
                                        disabled={disabled}
                                        label={
                                          <PaymentMethodCard
                                            flattened
                                            cardBrand={card.cardBrand}
                                            cardSummary={card.cardSummary}
                                            expiryMonth={card.expiryMonth}
                                            expiryYear={card.expiryYear}
                                            isDefault={card.isDefault}
                                          />
                                        }
                                      />
                                    ))}
                                  </RadioGroup>
                                </Stack>
                              )
                            }
                            disabled={disabled}
                          />
                          <Radio
                            alignItems="start"
                            variant="outline"
                            containerClassName={styles.paymentItem}
                            value={ADD_NEW_CARD_VALUE}
                            disabled={disabled}
                            label={adyenWithCvvMsg}
                          />
                        </>
                      )}
                      <Radio
                        variant="outline"
                        value="external"
                        label="Paid Externally (Cash / Wire / Other)"
                        alignItems="start"
                        containerClassName={styles.paymentItem}
                        labelSubtext={
                          isExternalPayment && (
                            <ExternalPaymentSection
                              disabled={disabled}
                              totalAmount={totalAmount}
                            />
                          )
                        }
                        disabled={disabled}
                      />
                      <Radio
                        alignItems="start"
                        variant="outline"
                        value="credit"
                        label="Pay by Customer Credit"
                        containerClassName={styles.paymentItem}
                        labelSubtext={
                          <>
                            <span className={styles.creditBalanceHint}>
                              {`${customerRemainingCreditLabel}: ${formatCurrency(availableCreditBalance, currencyCode)}`}
                            </span>
                            {paymentMethodChoice === 'credit' && (
                              <PartialPaymentInput
                                disabled={disabled}
                                totalAmount={totalAmount}
                                currencyCode={currencyCode}
                                isPartialPayment={isPartialPayment}
                                partialAmount={partialAmount}
                                onIsPartialPaymentChange={setIsPartialPayment}
                                onPartialAmountChange={setPartialAmount}
                              />
                            )}
                          </>
                        }
                        disabled={disabled}
                      />
                    </Stack>
                  </RadioGroup>
                </Stack>
              }
            />
          </>
        )}
      </DetailSection>
    </>
  );
};
